import type { INotice } from '@airbrake/browser';

// should we filter errors in ObservabilityAppService?
export class AirbrakeFiltersService {
  private readonly HTTP_BLOCKED_REQUEST = 'HTTP_BLOCKED_REQUEST';

  private readonly HTTP_UNAUTHORIZED_ERROR = 'HTTP_UNAUTHORIZED_ERROR';

  private readonly SENSITIVE_DATA_PATTERNS = {
    // eslint-disable-next-line no-useless-escape
    ONLINE_PAYMENT: /\b(?:\d{16}|\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}|\d{2}[-\/]\d{2})\b/g, //  Include patterns for credit card numbers, and expiration dates
    TOKEN: /\b[a-zA-Z0-9\-_]{10,}\.[a-zA-Z0-9\-_]{10,}\.[a-zA-Z0-9\-_]{10,}\b/g, // Token-like pattern (e.g., API tokens)
    BANK_ACCOUNT: /\b\d{9}\b/g, // Bank account numbers (9 digits)
    BANK_ACCOUNT_WITH_SPACES_OR_DASHES: /\b\d{6}[-\s]?\d{6}\b/g, // Bank account numbers with spaces or dashes
    EMAIL: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, // Email addresses
    SHA256_HASH: /\b[A-Fa-f0-9]{64}\b/g, // SHA-256 hash (64 characters hexadecimal)
  };

  private readonly NETWORK_ERROR = 'network error';

  private readonly EXTENSION_PATH = 'extension://';

  private readonly DISTUI_LERNA_LIB = 'distui-lerna-lib';

  private readonly DISTUI_GRAB_SCRIPT = 'distui/js/grab.js';

  private readonly LOCALHOST = '://localhost:';

  private readonly SECURE_LOCALHOST = 'secure.localhost.l1';

  public filterSensitiveData(notice: INotice) {
    const error = this.getError(notice);
    const context = notice.context || {};

    if (error && typeof error.message === 'string') {
      Object.keys(this.SENSITIVE_DATA_PATTERNS).forEach((key) => {
        error.message = this.replaceSensitiveData(error.message, key);
      });
    }

    Object.keys(context).forEach((key) => {
      if (typeof context[key] === 'string') {
        Object.keys(this.SENSITIVE_DATA_PATTERNS).forEach((patternKey) => {
          context[key] = this.replaceSensitiveData(context[key], patternKey);
        });
      }
    });

    return notice;
  }

  public filterVitestRunnerErrors(notice: INotice) {
    // @ts-expect-error: 'vitest' is not typed on ImportMeta or global
    const isVitest = (typeof globalThis !== 'undefined' && 'vitest' in globalThis) || typeof vitest !== 'undefined';

    if (isVitest) {
      return null;
    }

    return notice;
  }

  public filterLocalhost(notice: INotice) {
    const backtraceFile = this.getBacktraceFile(notice);

    if (!backtraceFile) {
      return notice;
    }

    if (backtraceFile.includes(this.LOCALHOST) || backtraceFile.includes(this.SECURE_LOCALHOST)) {
      return null;
    }

    return notice;
  }

  public filterHttpErrors(notice: INotice) {
    const error = this.getError(notice);

    if (!error) {
      return notice;
    }

    const isHttpError = error.type === this.HTTP_BLOCKED_REQUEST || error.type === this.HTTP_UNAUTHORIZED_ERROR;

    if (isHttpError) {
      return null;
    }

    return notice;
  }

  public filterNetworkErrors(notice: INotice) {
    const error = this.getError(notice);

    if (!error) {
      return notice;
    }

    if (error.message.toLowerCase().includes(this.NETWORK_ERROR)) {
      return null;
    }

    return notice;
  }

  public filterBrowserExtensionErrors(notice: INotice) {
    const backtraceFile = this.getBacktraceFile(notice);

    if (!backtraceFile) {
      return notice;
    }

    if (backtraceFile?.includes(this.EXTENSION_PATH)) {
      return null;
    }

    return notice;
  }

  public filterDistUIErrors(notice: INotice) {
    const backtraceFile = this.getBacktraceFile(notice);

    if (!backtraceFile) {
      return notice;
    }

    if (backtraceFile?.includes(this.DISTUI_LERNA_LIB) || backtraceFile?.includes(this.DISTUI_GRAB_SCRIPT)) {
      return null;
    }

    return notice;
  }

  private getBacktraceFile(notice: INotice) {
    return this.getError(notice)?.backtrace?.[0]?.file;
  }

  private getError(notice: INotice) {
    return notice.errors?.[0];
  }

  private replaceSensitiveData(input: string, key: string) {
    return input.replace(this.SENSITIVE_DATA_PATTERNS[key], `[${key}]`);
  }
}
